// ─────────────────────────────────────────────────────────────────────────────
//  contexts/BusinessContext.js — Business selection + auto-bootstrap
//
//  After login, this context:
//    1. Fetches the user's memberships via queries.my_memberships (secure RPC)
//    2. If none → auto-creates a default business via commands.create_business
//    3. Selects the default (or only) business
//    4. Exposes currentBusiness, businessId, isOwner, and switchBusiness()
//
//  Concurrency safety: auto-creation uses a stable idempotency key derived from
//  the user's UID, so two simultaneous app initializations will NOT create two
//  businesses — the second call gets the idempotent replay of the first.
//
//  Must wrap AppContext so that AppContext can read businessId for data loading.
// ─────────────────────────────────────────────────────────────────────────────
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { auth, onAuthStateChanged } from '../supabaseAuth';
import { fetchMyBusinesses, fetchProfile } from '../services/queries';
import { rpc } from '../services/rpc';

const BusinessContext = createContext(null);

export function BusinessProvider({ children }) {
  const [businesses, setBusinesses] = useState([]);
  const [currentBusiness, setCurrentBusiness] = useState(null);
  const [isOwner, setIsOwner] = useState(false);
  const [userRole, setUserRole] = useState(null); // system_key: 'OWNER','ADMIN','MANAGER', etc.
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [user, setUser] = useState(null);

  const token = user?.accessToken;
  const userId = user?.uid;

  // Helper: parse memberships array into business objects
  const parseMemberships = (memberships) =>
    (Array.isArray(memberships) ? memberships : []).map((m) => ({
      membershipId: m.id,
      businessId: m.business_id,
      roleId: m.role_id,
      isOwner: m.is_owner,
      roleName: m.role_name || null,
      roleSystemKey: m.role_system_key || (m.is_owner ? 'OWNER' : null),
      status: m.status,
      joinedAt: m.joined_at || null,
      name: m.business_name || 'Unnamed',
      country: m.business_country || 'TZ',
    }));

  // Helper: select a business and update derived state
  const selectBiz = (biz) => {
    setCurrentBusiness(biz);
    setIsOwner(biz?.isOwner || false);
    setUserRole(biz?.roleSystemKey || null);
  };

  // Subscribe to auth state
  useEffect(() => {
    return onAuthStateChanged(auth, (u) => {
      setUser(u || null);
      if (!u) {
        setBusinesses([]);
        setCurrentBusiness(null);
        setLoading(false);
      }
    });
  }, []);

  // Load businesses on login
  useEffect(() => {
    if (!token || !userId) return;

    let cancelled = false;

    const init = async () => {
      try {
        setLoading(true);
        setError(null);

        let memberships = await fetchMyBusinesses(token);

        // Auto-bootstrap: if user has no businesses, create one.
        // Idempotency key is stable per user (uid-based), so concurrent
        // initialization calls (multiple tabs, fast refresh) won't create
        // duplicate businesses — the second call replays the first's result.
        if (!memberships || !Array.isArray(memberships) || memberships.length === 0) {
          const displayName = user?.displayName || user?.email?.split('@')[0] || 'My Business';
          const stableKey = `biz-bootstrap-${userId}`;
          try {
            await rpc('create_business', stableKey, {
              name: `${displayName}'s Business`,
              country: 'TZ',
            }, token);
          } catch (err) {
            // If it fails with idempotency replay (already created), that's fine
            console.warn('[BusinessContext] auto-create:', err.code || err.message);
          }
          // Re-fetch memberships after creation (or replay)
          memberships = await fetchMyBusinesses(token);
        }

        if (cancelled) return;

        const bizList = parseMemberships(memberships);

        setBusinesses(bizList);

        // Select default business
        if (bizList.length > 0) {
          let defaultBiz = null;
          try {
            const profile = await fetchProfile(token);
            if (profile?.default_business_id) {
              defaultBiz = bizList.find((b) => b.businessId === profile.default_business_id);
            }
          } catch { /* ignore — just pick first */ }

          // Prefer: profile default → joined team (non-owner) → first in list
          const selected = defaultBiz || bizList.find((b) => !b.isOwner) || bizList[0];
          if (!cancelled) {
            selectBiz(selected);
          }
        }
      } catch (err) {
        if (!cancelled) {
          console.error('[BusinessContext] init error:', err);
          setError(err.message || 'Failed to load businesses');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    init();
    return () => { cancelled = true; };
  }, [token, userId, user?.displayName, user?.email]);

  const switchBusiness = useCallback((businessId) => {
    const biz = businesses.find((b) => b.businessId === businessId);
    if (biz) selectBiz(biz);
  }, [businesses]);

  // Refresh businesses from server (e.g. after joining a new business)
  // Optionally switch to a specific business by ID
  const refreshBusinesses = useCallback(async (switchToBusinessId) => {
    if (!token) return;
    try {
      const memberships = await fetchMyBusinesses(token);
      const bizList = parseMemberships(memberships);
      setBusinesses(bizList);
      if (switchToBusinessId) {
        const target = bizList.find((b) => b.businessId === switchToBusinessId);
        if (target) selectBiz(target);
      }
    } catch (err) {
      console.error('[BusinessContext] refresh error:', err);
    }
  }, [token]);

  const value = {
    businesses,
    currentBusiness,
    businessId: currentBusiness?.businessId || null,
    isOwner,
    userRole,
    loading,
    error,
    switchBusiness,
    refreshBusinesses,
    token, // exposed for hooks that need authenticated RPC calls (e.g. usePermissions)
  };

  return (
    <BusinessContext.Provider value={value}>
      {children}
    </BusinessContext.Provider>
  );
}

export const useBusiness = () => {
  const ctx = useContext(BusinessContext);
  if (!ctx) throw new Error('useBusiness must be used within BusinessProvider');
  return ctx;
};

export default BusinessContext;
